import { LoaderCircle, Users, UserX } from "lucide-react";
import {
  CRM_PERSON_OWNER_CUSTOM_FIELD_KEYS,
  CRM_PERSON_OWNER_LABEL_CUSTOM_FIELD_KEYS,
} from "@ease/crm/types";

export interface CrmContactOwnerSource {
  assignedTo?: string | null;
  assignedToLabel?: string | null;
}

export interface CrmContactOwnerMemberOption {
  id: string;
  name: string | null;
  email: string;
}

interface CrmContactOwnerAssignmentCardProps {
  visibleOwnerLabel: string | null;
  ownerDraftId: string;
  memberOptions: CrmContactOwnerMemberOption[];
  isOwnerDraftKnown: boolean;
  hasOwnerDraftChanged: boolean;
  isLoadingMembers: boolean;
  membersError: unknown;
  isSavingOwner: boolean;
  feedback: string | null;
  error: string | null;
  onOwnerDraftChange: (ownerId: string) => void;
  onSaveOwner: () => void;
  title?: string;
  unassignedLabel?: string;
}

export function CrmContactOwnerAssignmentCard({
  visibleOwnerLabel,
  ownerDraftId,
  memberOptions,
  isOwnerDraftKnown,
  hasOwnerDraftChanged,
  isLoadingMembers,
  membersError,
  isSavingOwner,
  feedback,
  error,
  onOwnerDraftChange,
  onSaveOwner,
  title = "Owner assignment",
  unassignedLabel = "Unassigned",
}: CrmContactOwnerAssignmentCardProps) {
  const isUnavailable = isLoadingMembers || Boolean(membersError) || isSavingOwner;

  return (
    <div className="mt-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-3">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--color-muted-foreground)]">
            {title}
          </p>
          <p className="mt-1 text-sm text-[var(--color-foreground)]">
            {visibleOwnerLabel ?? unassignedLabel}
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <select
            aria-label="Assign contact owner"
            value={ownerDraftId}
            onChange={(event) => onOwnerDraftChange(event.target.value)}
            disabled={isUnavailable}
            className="min-h-[40px] rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)] disabled:cursor-not-allowed disabled:opacity-60"
          >
            <option value="">
              {isLoadingMembers
                ? "Loading owners..."
                : membersError
                  ? "Owner list unavailable"
                  : unassignedLabel}
            </option>
            {!isOwnerDraftKnown ? (
              <option value={ownerDraftId}>{ownerDraftId}</option>
            ) : null}
            {memberOptions.map((member) => (
              <option key={member.id} value={member.id}>
                {formatCrmOwnerMemberLabel(member)}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => { onSaveOwner(); }}
            disabled={!hasOwnerDraftChanged || isSavingOwner || isLoadingMembers || Boolean(membersError)}
            className="inline-flex min-h-[40px] items-center justify-center gap-2 rounded-md border border-[var(--color-border)] px-3 py-2 text-sm font-medium text-[var(--color-foreground)] transition-colors hover:bg-[var(--color-background)] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSavingOwner
              ? <LoaderCircle className="h-4 w-4 animate-spin" />
              : ownerDraftId
                ? <Users className="h-4 w-4" />
                : <UserX className="h-4 w-4" />}
            Save owner
          </button>
        </div>
      </div>
      {membersError ? (
        <p className="mt-2 text-xs text-[var(--color-muted-foreground)]">
          Owner selection is temporarily unavailable. Existing ownership is preserved.
        </p>
      ) : null}
      {feedback ? (
        <p className="mt-2 text-xs text-[var(--color-success)]">{feedback}</p>
      ) : null}
      {error ? (
        <p className="mt-2 text-xs text-[var(--color-destructive)]">{error}</p>
      ) : null}
    </div>
  );
}

export function getCrmContactOwnerId(
  contact: CrmContactOwnerSource | null,
  customFields: Record<string, unknown>,
): string | null {
  return contact?.assignedTo?.trim()
    || crmContactStringField(customFields, "assignedTo")
    || crmContactStringField(customFields, "assigned_to")
    || crmContactStringField(customFields, "ownerId")
    || crmContactStringField(customFields, "owner_id");
}

export function getCrmContactOwnerLabel(
  contact: CrmContactOwnerSource | null,
  customFields: Record<string, unknown>,
  memberLabelById?: Map<string, string>,
): string | null {
  const ownerId = getCrmContactOwnerId(contact, customFields);
  return contact?.assignedToLabel?.trim()
    || crmContactStringField(customFields, "assignedToLabel")
    || crmContactStringField(customFields, "assigned_to_label")
    || crmContactStringField(customFields, "ownerLabel")
    || crmContactStringField(customFields, "ownerName")
    || crmContactStringField(customFields, "ownerEmail")
    || (ownerId ? memberLabelById?.get(ownerId) ?? ownerId : null);
}

export function formatCrmOwnerMemberLabel(member: { name: string | null; email: string }): string {
  return member.name?.trim() || member.email;
}

export function buildCrmContactOwnerCustomFields(
  currentCustomFields: Record<string, unknown>,
  owner: { assigneeId: string; assigneeLabel: string } | null,
): Record<string, unknown> {
  const nextCustomFields = { ...currentCustomFields };

  for (const key of CRM_PERSON_OWNER_CUSTOM_FIELD_KEYS) {
    delete nextCustomFields[key];
  }
  for (const key of CRM_PERSON_OWNER_LABEL_CUSTOM_FIELD_KEYS) {
    delete nextCustomFields[key];
  }

  if (owner) {
    nextCustomFields.assignedTo = owner.assigneeId;
    nextCustomFields.assignedToLabel = owner.assigneeLabel;
  }

  return nextCustomFields;
}

function crmContactStringField(record: Record<string, unknown>, key: string): string | null {
  const value = record[key];
  return typeof value === "string" && value.trim() ? value.trim() : null;
}
